/** Chooses one of a client's documents: type to filter, newest first. Replaces a long <select> of file names. */
import { ChevronDown, FileText, Search, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { matches } from "@/components/page";
import { formatDate } from "@/lib/format";
import type { Doc } from "@/lib/types";
import { cn } from "@/lib/utils";

// Enough to scroll through; a search narrows the rest.
const SHOWN = 50;

export function DocumentPicker({
  id,
  docs,
  value,
  onChange,
  placeholder = "None attached",
  allowNone = true,
  required,
  className,
}: {
  id?: string;
  docs: readonly Doc[] | undefined;
  value: string;
  onChange: (id: string) => void;
  /** Shown when nothing is chosen, and as the "none" choice when `allowNone`. */
  placeholder?: string;
  allowNone?: boolean;
  required?: boolean;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);
  const sorted = useMemo(() => [...(docs ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt)), [docs]);
  const found = sorted.filter((d) => matches(search, d.filename, d.folder, String(d.year)));
  const shown = found.slice(0, SHOWN);
  const choices: Array<Doc | null> = [...(allowNone && !search.trim() ? [null] : []), ...shown];
  const chosen = sorted.find((d) => d.id === value);

  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  useEffect(() => setActive(0), [search, open]);

  const pick = (d: Doc | null) => {
    onChange(d?.id ?? "");
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={box} className={cn("relative", className)}>
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-2 text-left text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className={cn("min-w-0 flex-1 truncate", !chosen && "text-muted-foreground")}>{chosen ? chosen.filename : value ? "Selected document" : placeholder}</span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
      </button>
      {/* Keeps a native "required" check on the form. */}
      {required && <input tabIndex={-1} aria-hidden required value={value} onChange={() => {}} className="pointer-events-none absolute inset-x-0 bottom-0 h-px opacity-0" />}
      {open && (
        <div className="mt-1 rounded-md border border-border bg-popover p-1 shadow-md">
          <label className="relative block">
            <span className="sr-only">Search documents</span>
            <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <input
              autoFocus
              type="search"
              value={search}
              placeholder={`Search ${sorted.length} document${sorted.length === 1 ? "" : "s"}`}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "ArrowDown") {
                  e.preventDefault();
                  setActive((i) => Math.min(i + 1, choices.length - 1));
                } else if (e.key === "ArrowUp") {
                  e.preventDefault();
                  setActive((i) => Math.max(i - 1, 0));
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  if (choices[active] !== undefined) pick(choices[active]!);
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpen(false);
                }
              }}
              className="h-8 w-full rounded border border-input bg-background pl-8 pr-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </label>
          <ul role="listbox" aria-label="Documents" className="mt-1 max-h-60 overflow-y-auto">
            {choices.map((d, i) => (
              <li key={d?.id ?? "none"} role="option" aria-selected={(d?.id ?? "") === value}>
                <button
                  type="button"
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(d)}
                  className={cn("flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm", i === active && "bg-muted", (d?.id ?? "") === value && "font-medium")}
                >
                  {d ? (
                    <>
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate">{d.filename}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {d.year} · {d.folder} · added {formatDate(d.createdAt)}
                        </span>
                      </span>
                    </>
                  ) : (
                    <>
                      <X className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                      <span className="text-muted-foreground">{placeholder}</span>
                    </>
                  )}
                </button>
              </li>
            ))}
            {!found.length && <li className="px-2 py-3 text-center text-sm text-muted-foreground">{sorted.length ? "No documents match." : "This client has no documents yet."}</li>}
          </ul>
          {found.length > SHOWN && (
            <p className="border-t border-border px-2 py-1.5 text-xs text-muted-foreground">
              Showing the newest {SHOWN} of {found.length}. Type to narrow the list.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
